import CloseOutline16 from '@carbon/icons-react/es/close--outline/16';
import Redo16 from '@carbon/icons-react/es/redo/16';
import { Popover, Skeleton, Typography } from 'antd';
import moment, { Moment } from 'moment';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PermissionSetType, User } from 'api/models';
import {
  Configuration,
  TimesheetApprovalType,
} from 'api/timesheets/configuration/configuration';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { Button } from 'components/atoms/button';
import { Modal } from 'components/atoms/modal';
import { TableSkeleton } from 'components/atoms/table';
import { useHeaderHeight } from 'components/molecules/page';
import { ApprovedSeal } from 'components/organisms/timesheets/common/approved-seal';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import {
  BillabilitySummary,
  BodyWrapper,
  Footer,
  FooterActionRow,
  HeaderSummary,
  MetricSummary,
  Wrapper,
} from 'components/organisms/timesheets/common/style';
import { UtilizationTitle } from 'components/organisms/timesheets/common/summary';
import {
  calculateUtilization,
  getDays,
  getTotalBillableMinutes,
  getTotalMinutesFromEntries,
  getTotalNonBillableMinutes,
} from 'components/organisms/timesheets/common/utils';
import {
  getCapacityInHoursForADay,
  getCapacityInMinutesForADay,
} from 'components/organisms/user-capacity';
import { useUserPermissionSetType } from 'hooks/permissions';
import { fullName } from 'utils/full-name';
import { getTimeOffDaysInAWeek } from 'utils/time-off-utils';
import { EmptyState as EmptyTimesheet } from '../common/empty-state';
import { RejectCommentDrawer } from '../drawers';
import { DrawerType, FreezeInfoNote, RejectInfo } from '../my-timesheet';
import { SingleUserViewTable, SummaryRow } from '../single-user-view-table';
import { useTimesheetActions } from '../timesheets-actions';
import { RejectNoteType, TimesheetStatus } from '../types';
import { RejectNoteForm } from './reject-note-form/reject-note-form';

const WidgetLoader = styled(Skeleton)`
  width: 150px;
  margin-right: var(--spacing-06);
`;

const HeaderSummaryDetails = ({
  timesheetEntries,
  user,
  configuration,
  numberOfTimeOffDays,
}) => {
  const { capacity } = user;
  const totalMinutes = getTotalMinutesFromEntries(timesheetEntries);
  const billableMinutes = getTotalBillableMinutes(timesheetEntries);
  const nonBillableMinutes = getTotalNonBillableMinutes(timesheetEntries);
  const capacityAfterTimeOff =
    capacity -
    numberOfTimeOffDays *
      getCapacityInHoursForADay(capacity, configuration.workingDays);
  const utilization = calculateUtilization(
    billableMinutes,
    capacityAfterTimeOff * 60,
  );

  return (
    <HeaderSummary>
      <BillabilitySummary
        billableMinutes={billableMinutes}
        nonBillableMinutes={nonBillableMinutes}
        capacityInMinutes={
          (capacity -
            getCapacityInHoursForADay(capacity, configuration.workingDays)) *
          60
        }
      />
      <MetricSummary
        title={<FormattedMessage id="timesheets.summary.total_time_tracked" />}
        value={<FormatTime value={totalMinutes} showHypenIfZero={false} />}
      />
      <MetricSummary
        title={
          <FormattedMessage
            id="timesheets.summary.user_capacity"
            values={{ userName: user.firstName ?? user.lastName }}
          />
        }
        value={`${capacityAfterTimeOff}h`}
      />
      {numberOfTimeOffDays > 0 && (
        <MetricSummary
          title={<FormattedMessage id="settings.profile.timeoff.title" />}
          value={`${
            numberOfTimeOffDays *
            getCapacityInHoursForADay(capacity, configuration.workingDays)
          }h`}
        />
      )}
      <MetricSummary
        title={<UtilizationTitle />}
        value={`${utilization.toFixed(1)}%`}
      />
    </HeaderSummary>
  );
};

const ApprovedLabel = ({ isProjectOwnerApproval }) => {
  const PAGE_HEADER_HEIGHT = useHeaderHeight();
  const TOP_BAR_HEIGHT = 48;
  const HALF_OF_WIDGET_ROW_HEIGHT = 52;
  const EXTRA_SPACE = 12;
  const RIGHT_COMMENT_SPACE = 160;

  return isProjectOwnerApproval ? (
    <ApprovedSeal top={PAGE_HEADER_HEIGHT} right={RIGHT_COMMENT_SPACE} />
  ) : (
    <ApprovedSeal
      top={
        PAGE_HEADER_HEIGHT +
        EXTRA_SPACE +
        HALF_OF_WIDGET_ROW_HEIGHT +
        TOP_BAR_HEIGHT
      }
      right={RIGHT_COMMENT_SPACE}
    />
  );
};

const RejectAllButton = ({
  rejectTimesheet,
  timesheet,
  setRejectNoteVisible,
  rejectNoteVisible,
}) => (
  <Popover
    content={
      <RejectNoteForm
        onReject={rejectTimesheet}
        projectId={timesheet?.project?.id}
        closePopup={() => setRejectNoteVisible(false)}
      />
    }
    visible={rejectNoteVisible}
    onVisibleChange={(visible) => setRejectNoteVisible(visible)}
    trigger="click"
    placement="topRight"
  >
    <Button
      icon={<CloseOutline16 />}
      iconPlacement="right"
      type="tertiary"
      danger
    >
      <FormattedMessage id="timesheets.feature.reject.all" />
    </Button>
  </Popover>
);

const ApproveAllButton = ({
  isStatusChangeInProgress,
  timeSheetApproveAll,
}) => (
  <Button
    icon={<ApproveOutline16 />}
    type="primary"
    iconPlacement="right"
    loading={isStatusChangeInProgress}
    onClick={timeSheetApproveAll}
  >
    <FormattedMessage id="timesheets.feature.approve.all" />
  </Button>
);

const WithdrawAllButtonWithModal = ({
  user,
  isStatusChangeInProgress,
  setConfirmationPopupVisible,
  confirmationPopupVisible,
  timesheetWithdrawAllApproval,
}) => (
  <>
    <Button
      icon={<Redo16 />}
      type="primary"
      iconPlacement="right"
      loading={isStatusChangeInProgress}
      onClick={() => setConfirmationPopupVisible(true)}
      danger
    >
      <FormattedMessage id="timesheets.buttons.withdraw_approval" />
    </Button>
    <Modal
      visible={confirmationPopupVisible}
      title={<FormattedMessage id="timesheets.text.withdraw_approval" />}
      confirmLoading={false}
      okType="danger"
      okText={<FormattedMessage id="timesheets.buttons.withdraw" />}
      onCancel={() => setConfirmationPopupVisible(false)}
      onOk={() => {
        setConfirmationPopupVisible(false);
        timesheetWithdrawAllApproval();
      }}
    >
      <p>
        <FormattedMessage
          id="timesheets.text.withdraw_approval_content_approver"
          values={{
            b: (chunks) => (
              <Typography.Text strong>
                {fullName(user) ?? chunks}
              </Typography.Text>
            ),
          }}
        />
      </p>
    </Modal>
  </>
);

interface Props {
  startDate: Moment;
  configuration: Configuration;
  user: User;
  projectId?: number;
  visibleDrawer: DrawerType | undefined;
  setVisibleDrawer: (DrawerType: DrawerType | undefined) => void;
  rejectNotes?: RejectNoteType[];
}

export const TimesheetApprovalSingleUserView: FC<Props> = ({
  startDate,
  configuration,
  user,
  projectId,
  visibleDrawer,
  setVisibleDrawer,
  rejectNotes,
}) => {
  const [confirmationPopupVisible, setConfirmationPopupVisible] =
    useState(false);
  const [rejectNoteVisible, setRejectNoteVisible] = useState(false);
  const IS_MY_TIMESHEET = false;
  const {
    timesheet,
    timesheetWeekRows,
    timeEntryCategories,
    timeSheetSummary,
    timesheetEntries,
    isTimesheetLoading,
    approveTask,
    rejectTask,
    withdrawTask,
    approveTimesheet,
    rejectTimesheet,
    withdrawTimesheet,
    sendReminder,
    updateTimeEntryInTask,
    addTimeEntryInTask,
    deleteTimeEntryInTask,
    isStatusChangeInProgress,
    categories,
    projects,
  } = useTimesheetActions(startDate, user, IS_MY_TIMESHEET, projectId);

  const numberOfTimeOffDays = getTimeOffDaysInAWeek(
    startDate,
    user?.userTimeOffDetails,
  );

  const isWeekFreezed = useMemo(() => {
    if (configuration.lockSetting.freeze.enabled) {
      const currentWeekDays = getDays(startDate, 7).map((x) =>
        x.format('YYYY-MM-DD'),
      );

      return (
        currentWeekDays.includes(
          configuration.lockSetting.freeze.enableAfter ?? '',
        ) || startDate < moment(configuration.lockSetting.freeze.enableAfter)
      );
    }

    return false;
  }, [
    configuration.lockSetting.freeze.enableAfter,
    configuration.lockSetting.freeze.enabled,
    startDate,
  ]);

  const isWeekFreezedAfter = useMemo(
    () => configuration.lockSetting.freeze.enableAfter,
    [configuration.lockSetting.freeze.enableAfter],
  );

  const { capacity } = user;

  const userPermissionType = useUserPermissionSetType();
  const isUserAdmin = userPermissionType === PermissionSetType.AccountAdmin;

  const isInEditable =
    timesheet?.status !== TimesheetStatus.Approved &&
    (!isWeekFreezed || isUserAdmin);

  const isProjectOwnerApproval = useMemo(
    () => configuration.approvalSetting === TimesheetApprovalType.ProjectOwner,
    [configuration.approvalSetting],
  );

  const timeSheetApproveAll = useCallback(() => {
    if (isProjectOwnerApproval && timesheet?.project) {
      approveTimesheet(timesheet?.project.id);
    } else {
      approveTimesheet();
    }
  }, [approveTimesheet, isProjectOwnerApproval, timesheet?.project]);

  const timesheetWithdrawAllApproval = useCallback(() => {
    if (isProjectOwnerApproval && timesheet?.project) {
      withdrawTimesheet(timesheet?.project.id);
    } else {
      withdrawTimesheet();
    }
  }, [isProjectOwnerApproval, timesheet?.project, withdrawTimesheet]);

  const isAllNotSubmitted = useMemo(() => {
    return !timesheetWeekRows?.find(
      (row) => row.task.approvalStatus !== TimesheetStatus.NotSubmitted,
    );
  }, [timesheetWeekRows]);

  return (
    <Wrapper>
      <RejectCommentDrawer
        visible={visibleDrawer === DrawerType.RejectComment}
        onClose={() => {
          setVisibleDrawer(undefined);
        }}
        rejectNotes={rejectNotes}
      />
      {!isProjectOwnerApproval &&
        (isTimesheetLoading ? (
          <HeaderSummary>
            <WidgetLoader
              title={false}
              paragraph={{ rows: 2, width: ['100%', '80%'] }}
            />
            <WidgetLoader
              title={false}
              paragraph={{ rows: 2, width: ['100%', '80%'] }}
            />
            <WidgetLoader
              title={false}
              paragraph={{ rows: 2, width: ['100%', '80%'] }}
            />
          </HeaderSummary>
        ) : (
          <>
            {timesheetEntries.length === 0 ? null : (
              <HeaderSummaryDetails
                timesheetEntries={timesheetEntries}
                user={user}
                configuration={configuration}
                numberOfTimeOffDays={numberOfTimeOffDays}
              />
            )}
          </>
        ))}
      <BodyWrapper>
        {isWeekFreezed && (
          <FreezeInfoNote isWeekFreezedAfter={isWeekFreezedAfter} />
        )}
        {isTimesheetLoading ? (
          <TableSkeleton columnCount={8} />
        ) : (
          <>
            {timesheetEntries.length === 0 ? (
              <EmptyTimesheet
                sendReminder={sendReminder}
                userName={fullName(user)}
              />
            ) : (
              <>
                {timesheet?.status === TimesheetStatus.Approved ? (
                  <ApprovedLabel
                    isProjectOwnerApproval={isProjectOwnerApproval}
                  />
                ) : null}
                <SingleUserViewTable
                  startDate={startDate}
                  workingDays={configuration.workingDays}
                  editable={isInEditable}
                  timeEntryCategories={timeEntryCategories ?? []}
                  updateTimeEntryInTask={updateTimeEntryInTask}
                  addTimeEntryInTask={addTimeEntryInTask}
                  deleteTimeEntryInTask={deleteTimeEntryInTask}
                  timesheetRowData={timesheetWeekRows ?? []}
                  user={user}
                  categories={categories}
                  timesheetEntriesConfig={configuration.enabledSourceTypes}
                  approvalSetting={configuration.approvalSetting}
                  projects={projects}
                  approveTask={approveTask}
                  rejectTask={rejectTask}
                  withdrawTask={withdrawTask}
                  isWeekFreezed={isWeekFreezed}
                  isUserAdmin={isUserAdmin}
                  mandateCategory={configuration.configMeta.categoryMandatory}
                />
              </>
            )}
          </>
        )}
      </BodyWrapper>
      {isTimesheetLoading ? null : (
        <Footer>
          {timesheetEntries.length === 0 ? null : (
            <SummaryRow
              capacityPerDayInMinutes={getCapacityInMinutesForADay(
                capacity,
                configuration.workingDays,
              )}
              workingDays={configuration.workingDays}
              startDate={startDate}
              user={user}
              timeSheetSummary={timeSheetSummary}
              approvalSetting={configuration.approvalSetting}
            />
          )}

          <FooterActionRow isUserApproverView>
            {!isAllNotSubmitted ? (
              <>
                {isUserAdmin || !isWeekFreezed ? (
                  <>
                    {timesheet?.status === TimesheetStatus.Rejected ? (
                      <RejectInfo />
                    ) : null}
                    {timesheet?.status !== TimesheetStatus.Rejected ? (
                      <RejectAllButton
                        rejectTimesheet={rejectTimesheet}
                        timesheet={timesheet}
                        setRejectNoteVisible={setRejectNoteVisible}
                        rejectNoteVisible={rejectNoteVisible}
                      />
                    ) : null}
                    {timesheet?.status !== TimesheetStatus.Approved ? (
                      <ApproveAllButton
                        isStatusChangeInProgress={isStatusChangeInProgress}
                        timeSheetApproveAll={timeSheetApproveAll}
                      />
                    ) : null}
                    {timesheet?.status === TimesheetStatus.Approved ? (
                      <WithdrawAllButtonWithModal
                        user={user}
                        isStatusChangeInProgress={isStatusChangeInProgress}
                        setConfirmationPopupVisible={
                          setConfirmationPopupVisible
                        }
                        confirmationPopupVisible={confirmationPopupVisible}
                        timesheetWithdrawAllApproval={
                          timesheetWithdrawAllApproval
                        }
                      />
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}
          </FooterActionRow>
        </Footer>
      )}
    </Wrapper>
  );
};
